import { useEffect, useState } from "react";
import { Bot, Pencil, Plus, Power, X } from "lucide-react";
import { api } from "../api";
import type { AgentRecord, ModelPolicy } from "../../shared/types";

const emptyAgent: Omit<AgentRecord, "id" | "updatedAt"> = {
  name: "",
  handle: "",
  description: "",
  provider: "demo",
  model: "",
  modelPolicy: "balanced",
  instructions: "",
  tools: [],
  budget: 1,
  active: true,
};

const policyLabel: Record<ModelPolicy, string> = {
  economical: "Econômico",
  balanced: "Balanceado",
  deep: "Profundo",
};

export function AgentsView() {
  const [agents, setAgents] = useState<AgentRecord[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...emptyAgent });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    const result = await api.agents();
    setAgents(result.agents);
  }

  useEffect(() => { void load(); }, []);

  function newAgent() {
    setEditingId(null);
    setDraft({ ...emptyAgent });
    setOpen(true);
  }

  function edit(agent: AgentRecord) {
    setEditingId(agent.id);
    setDraft({
      name: agent.name,
      handle: agent.handle,
      description: agent.description,
      provider: agent.provider,
      model: agent.model,
      modelPolicy: agent.modelPolicy,
      instructions: agent.instructions,
      tools: agent.tools,
      budget: agent.budget,
      active: agent.active,
    });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editingId) await api.updateAgent(editingId, draft);
      else await api.createAgent(draft);
      await load();
      setOpen(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="workspace-page">
      <header className="page-header">
        <div><p className="eyebrow">Registro</p><h1>Agentes</h1><p>{agents.filter((agent) => agent.active).length} agentes ativos</p></div>
        <button className="button button--primary" onClick={newAgent}><Plus size={15} />Novo agente</button>
      </header>

      <section className="agent-grid" aria-label="Agentes">
        {agents.map((agent) => (
          <article className="agent-card" key={agent.id}>
            <div className="agent-card__header"><span className="agent-card__icon"><Bot size={17} /></span><span className={`active-indicator ${agent.active ? "is-active" : ""}`}><Power size={12} />{agent.active ? "Ativo" : "Inativo"}</span></div>
            <h2>{agent.name}</h2>
            <p>{agent.description}</p>
            <dl><div><dt>Política</dt><dd>{policyLabel[agent.modelPolicy]}</dd></div><div><dt>Provedor</dt><dd>{agent.provider === "demo" ? "Demo local" : agent.provider}</dd></div><div><dt>Orçamento</dt><dd>R$ {agent.budget.toFixed(2).replace(".", ",")}</dd></div></dl>
            <button className="button button--secondary button--wide" onClick={() => edit(agent)}><Pencil size={14} />Editar agente</button>
          </article>
        ))}
      </section>

      {open && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <div className="modal agent-modal" role="dialog" aria-modal="true" aria-labelledby="agent-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Registro de agentes</p><h2 id="agent-title">{editingId ? "Editar agente" : "Novo agente"}</h2></div><button className="icon-button" aria-label="Fechar" onClick={() => setOpen(false)}><X size={17} /></button></div>
            <div className="form-grid">
              <label><span>Nome</span><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
              <label><span>Menção</span><div className="input-with-prefix"><span>@</span><input value={draft.handle} onChange={(event) => setDraft({ ...draft, handle: event.target.value })} placeholder="nome-do-agente" /></div></label>
              <label><span>Descrição</span><input value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} /></label>
              <label><span>Provedor</span><select value={draft.provider} onChange={(event) => setDraft({ ...draft, provider: event.target.value as AgentRecord["provider"] })}><option value="demo">Demo local</option><option value="openai-compatible">OpenAI compatível</option><option value="codex-cli">Codex CLI</option><option value="claude-cli">Claude CLI</option><option value="grok-cli">Grok CLI</option><option value="hermes-cli">Hermes</option></select></label>
              <label><span>Política</span><select value={draft.modelPolicy} onChange={(event) => setDraft({ ...draft, modelPolicy: event.target.value as ModelPolicy })}><option value="economical">Econômico</option><option value="balanced">Balanceado</option><option value="deep">Profundo</option></select></label>
              <label><span>Modelo</span><input value={draft.model} onChange={(event) => setDraft({ ...draft, model: event.target.value })} placeholder="Opcional" /></label>
              <label><span>Orçamento</span><input type="number" min="0" step="0.1" value={draft.budget} onChange={(event) => setDraft({ ...draft, budget: Number(event.target.value) })} /></label>
              <label className="form-grid__wide"><span>Instruções</span><textarea rows={7} value={draft.instructions} onChange={(event) => setDraft({ ...draft, instructions: event.target.value })} /></label>
              <label className="checkbox-row form-grid__wide"><input type="checkbox" checked={draft.active} onChange={(event) => setDraft({ ...draft, active: event.target.checked })} /><span>Agente ativo</span></label>
            </div>
            <div className="modal-actions"><button className="button button--secondary" onClick={() => setOpen(false)}>Cancelar</button><button className="button button--primary" disabled={!draft.name.trim() || saving} onClick={() => void save()}>Salvar agente</button></div>
          </div>
        </div>
      )}
    </main>
  );
}
